// src/components/landing/ListingCard.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, Star, Zap, ArrowRight, Navigation, Check, ShoppingCart } from "lucide-react";
import { BrandListing } from "../../types/brandlistingsmodel";
import { ListingImage } from "./ListingImage";
import { mediaIcon } from "./mediaIcons";
import { useCart } from "../../context/CartContext";
import { AdSpace } from "../../types";
import {
  formatINR,
  availabilityLabel,
  locationTone,
  mediaTypeLabel,
  hasValidCoordinates,
  googleMapsLink,
} from "../../utils/listingFormat";

export function ListingCard({ listing }: { listing: BrandListing }) {
  const Icon = mediaIcon(listing.mediaType);
  const cart = useCart();
  const [added, setAdded] = useState(false);
  const showMap = hasValidCoordinates(listing.latitude, listing.longitude);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // The shared cart (also used by the internal /brand flow) expects an
    // AdSpace. This BrandListing has different field names — cast is
    // deliberate; the cart only ever re-reads this object through
    // utils/cartDisplay.ts, which reads both shapes defensively.
    cart.addToCart(listing as unknown as AdSpace);
    setAdded(true);
    setTimeout(() => setAdded(false), 1600);
  };

  return (
    <div className="group overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-[#1D9BD8]/40 hover:shadow-[0_24px_50px_-24px_rgba(15,23,42,0.25)]">
      <Link to={`/listings/${listing.id}`} className="relative block aspect-[4/3] w-full bg-slate-100">
        <ListingImage photos={listing.photos} alt={listing.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/0 to-black/0" />

        <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-medium text-slate-700 backdrop-blur">
          <Icon className="h-3.5 w-3.5" /> {mediaTypeLabel(listing.mediaType)}
        </div>
        {listing.locationType.name?.startsWith("A+") && (
          <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-[#EB4A2D] px-2.5 py-1 text-[11px] font-semibold text-white">
            <Star className="h-3 w-3 fill-white" /> Prime
          </div>
        )}
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
          <span className="rounded-lg bg-black/50 px-2 py-1 text-[11px] text-white backdrop-blur">{listing.dimensions}</span>
          {listing.locationType.name && (
            <span className={`rounded-lg px-2 py-1 text-[11px] font-medium ${locationTone(listing.locationType.name)}`}>
              {listing.locationType.name.replace(" Location", "")}
            </span>
          )}
        </div>
      </Link>

      <div className="p-4">
        <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-slate-400">
          <MapPin className="h-3 w-3" /> {listing.area}, {listing.city}
        </div>
        <Link to={`/listings/${listing.id}`}>
          <h3 className="font-display mt-1 text-lg leading-tight text-[#0F172A] hover:text-[#1D9BD8]">{listing.title}</h3>
        </Link>
        {listing.landmark && <p className="mt-0.5 text-xs text-slate-400">Near {listing.landmark}</p>}

        <div className="mt-3 flex items-center gap-4 text-[12px] text-slate-500">
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" /> {availabilityLabel(listing.availableFrom, listing.availableTo)}
          </span>
        </div>
        {listing.locationType.trafficLevel && (
          <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-slate-500">
            <Zap className="h-3.5 w-3.5 text-[#1D9BD8]" /> {listing.locationType.trafficLevel} traffic
          </span>
        )}

        {showMap && (
          <a
            href={googleMapsLink(listing.latitude, listing.longitude)}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#1170A3] hover:underline"
          >
            <Navigation className="h-3 w-3" /> View on Google Maps
          </a>
        )}

        <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
          <div>
            <p className="font-display text-xl text-[#0F172A]">{formatINR(listing.priceBreakdown.totalPayable)}</p>
            <p className="text-[11px] text-slate-400">
              {formatINR(listing.priceBreakdown.providerRent)} + {listing.priceBreakdown.gstPercent}% GST
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleAddToCart}
              className={`flex items-center gap-1 rounded-full px-3.5 py-2 text-[12px] font-semibold transition-colors ${
                added ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-[#EB4A2D] hover:text-white"
              }`}
            >
              {added ? <Check className="h-3.5 w-3.5" /> : <ShoppingCart className="h-3.5 w-3.5" />}
              {added ? "Added" : "Add to cart"}
            </button>
            <Link
              to={`/listings/${listing.id}`}
              className="flex items-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-[12px] font-semibold text-slate-700 hover:border-slate-300"
            >
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}